import { TextField as MuiTextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import Grid from '@mui/material/Grid'
import {
  getDbSDatasetInstructionProperty,
  getReturnIdsFromDatasetInstruction,
  isDbSDatasetInstructionContract,
  serializeDatasetInstruction,
} from 'helpers/dataset.helper/dataset.helper'
import { clone, isBoolean, isObject } from 'helpers/global.helper/global.helper'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { Button, Inline, RecordInput, Select, Switch, TextField, Toggle } from 'ui/atoms'
import { fetchDatasetsFromDbS } from 'utils/dbs/dbs'
import { useSnapshot } from 'valtio'

import styles from './AddDatasetForm.module.less'

type AddDatasetFormProps = {
  onSubmit: (datasetInstruction: DatasetInstruction) => void
}

type SupportedDatasetTypes = 'dbs' | 'calc'

type FormDatasetInstructionContract = DatasetContractDbS | DatasetContractCalc

type FormDatasetInstruction = Omit<DatasetInstruction, 'contract'> & {
  contract: FormDatasetInstructionContract
}

export default function AddDatasetForm(props: AddDatasetFormProps): ReactElement {
  const [loadingState, setLoadingState] = useState<'' | 'pending' | 'error' | 'success'>('')
  const { t } = useTranslation()

  const uiConfigSnap = useSnapshot(uiConfigStore)

  const [datasetInstruction, setDatasetInstruction] = useState<FormDatasetInstruction>({
    type: (uiConfigSnap.uiConfigEditorDefaultValues.type as SupportedDatasetTypes) || `dbs`,
    return_id: ``,
    contract: {
      metadata_filter: uiConfigSnap.uiConfigEditorDefaultValues?.metadata_filter
        ? JSON.parse(uiConfigSnap.uiConfigEditorDefaultValues.metadata_filter)
        : {
            include: {
              organization: 'sigholm',
              system: 'demostaden',
              internal_id: 'temperature',
              abs_service: 'abs_weather',
            },
            exclude: {},
          },
      id_renaming: {},
      global_read_api: false,
      variables: [],
      operator: `+`,
      element_wise: true,
    },
    filter: {
      start_time: `$start_time`,
      end_time: `$end_time`,
      offset_start_time: `$offset_start_time`,
      offset_end_time: `$offset_end_time`,
      created_at: '$created_at',
      aggregate: ``,
    },
  })
  const contractCalc = datasetInstruction.contract as DatasetContractCalc

  function onSubmit(): void {
    const serializedDatasetInstruction = serializeDatasetInstruction(datasetInstruction as DatasetInstruction)
    props.onSubmit(serializedDatasetInstruction)

    // Update default values
    uiConfigStore.uiConfigEditorDefaultValues.type = datasetInstruction.type as SupportedDatasetTypes
    uiConfigStore.uiConfigEditorDefaultValues.metadata_filter = JSON.stringify(
      getDbSDatasetInstructionProperty(datasetInstruction?.contract, 'metadata_filter', {}, isObject)
    )
  }

  return (
    <div className={styles.AddDatasetForm}>
      <Inline spaceBetween>
        <h2>{t(`Add dataset`)}</h2>
        <Switch
          value={datasetInstruction.type}
          onClick={(type) => setDatasetInstruction({ ...datasetInstruction, type })}
          items={[
            {
              label: `dbs`,
              value: `dbs`,
            },
            {
              label: `calc`,
              value: `calc`,
            },
          ]}
        />
      </Inline>

      <h3>{t(`Contract`)}</h3>
      {/* DbS */}
      {datasetInstruction.type === 'dbs' && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <pre>metadata_filter.include</pre>
            <RecordInput
              value={
                getDbSDatasetInstructionProperty(datasetInstruction?.contract, 'metadata_filter', {}, isObject).include
              }
              onChange={(record) => {
                const updatedInstruction = clone(datasetInstruction)
                const updatedInstructionContract = updatedInstruction.contract as DatasetContractDbS

                if (!updatedInstructionContract.metadata_filter) {
                  updatedInstructionContract.metadata_filter = {
                    include: {},
                    exclude: {},
                  }
                }

                updatedInstructionContract.metadata_filter.include = record as Record<string, string>
                setDatasetInstruction(updatedInstruction)
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <pre>metadata_filter.exclude</pre>
            <RecordInput
              value={
                getDbSDatasetInstructionProperty(datasetInstruction?.contract, 'metadata_filter', {}, isObject).exclude
              }
              onChange={(record) => {
                const updatedInstruction = clone(datasetInstruction)
                const updatedInstructionContract = updatedInstruction.contract as DatasetContractDbS

                if (!updatedInstructionContract.metadata_filter) {
                  updatedInstructionContract.metadata_filter = {
                    include: {},
                    exclude: {},
                  }
                }
                updatedInstructionContract.metadata_filter.exclude = record
                setDatasetInstruction(updatedInstruction)
              }}
            />
          </Grid>

          {/* On non-produciton environment, this setting allow reading data from production database. */}
          {process.env.REACT_APP_STAGE !== `production` && (
            <Grid size={{ xs: 12 }}>
              <Toggle
                label={t(`Global read API`)}
                value={getDbSDatasetInstructionProperty(
                  datasetInstruction?.contract,
                  'global_read_api',
                  false,
                  isBoolean
                )}
                onClick={(global_read_api) =>
                  setDatasetInstruction({
                    ...datasetInstruction,
                    contract: { ...datasetInstruction.contract, global_read_api },
                  })
                }
              />

              <p>
                {t(
                  `If enabled, the DbS API token used will be the global read API token with data from AbS Prod. This is useful for fetching datasets like measurements, forecast, electricity prices and weather.`
                )}
              </p>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <pre>id_renaming</pre>
            <RecordInput
              value={getDbSDatasetInstructionProperty(datasetInstruction?.contract, 'id_renaming', {}, isObject)}
              onChange={(record) => {
                const updatedInstruction = clone(datasetInstruction)
                const updatedContract = updatedInstruction.contract as DatasetContractDbS
                updatedContract.id_renaming = record
                setDatasetInstruction(updatedInstruction)
              }}
            />
            <Button
              secondary
              disabled={
                Object.keys(
                  isDbSDatasetInstructionContract(datasetInstruction.contract)
                    ? datasetInstruction.contract.metadata_filter?.include
                    : {}
                ).length === 0 || loadingState === 'pending'
              }
              onClick={async () => {
                setLoadingState('pending')

                try {
                  const queryInstruction = clone(datasetInstruction)
                  if (!queryInstruction?.filter) {
                    queryInstruction.filter = {}
                  }
                  queryInstruction.filter.aggregate = 'latest'
                  const datasets = await fetchDatasetsFromDbS([queryInstruction as DatasetInstruction])

                  const updatedInstruction = clone(datasetInstruction)
                  const updatedContract = updatedInstruction.contract as DatasetContractDbS
                  updatedContract.id_renaming = datasets.reduce((acc, dataset) => {
                    return {
                      ...acc,
                      [dataset.return_id]: dataset.return_id,
                    }
                  }, {})
                  setDatasetInstruction(updatedInstruction)

                  setLoadingState('success')
                } catch (_err: any) {
                  setLoadingState('error')
                }
              }}
            >
              {t('Fetch datasets & populate id_renaming')}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Calc */}
      {datasetInstruction.type === `calc` && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 4 }}>
            <Autocomplete
              multiple
              freeSolo
              autoSelect
              options={(uiConfigSnap.editingUiConfig?.dataset_instructions ?? [])
                .map((datasetInstruction) => {
                  const returnIds = getReturnIdsFromDatasetInstruction(datasetInstruction)

                  return returnIds.map((returnId) => ({
                    value: returnId,
                    index: returnId,
                  }))
                })
                .flat()}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option?.value)}
              onChange={(_e, newValue) => {
                const variables = newValue.map((value) => {
                  if (Number(value)) {
                    return Number(value)
                  }

                  // If value is a string, use it. If it's an object, use its value property.
                  return typeof value === 'string' ? value : value?.value
                })
                setDatasetInstruction({
                  ...datasetInstruction,
                  contract: { ...contractCalc, variables },
                })
              }}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  error={(contractCalc.variables?.length ?? 0) < 2}
                  fullWidth
                  aria-label={`Variabler`}
                  label={t(`Variables`)}
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Toggle
              label={t(`Element wise`)}
              value={contractCalc.element_wise === true}
              onClick={(element_wise) =>
                setDatasetInstruction({
                  ...datasetInstruction,
                  contract: { ...contractCalc, element_wise },
                })
              }
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Select
              label={t(`Operator`)}
              value={contractCalc.operator ?? `+`}
              onChange={(operator) =>
                setDatasetInstruction({
                  ...datasetInstruction,
                  contract: { ...contractCalc, operator: operator as `+` | `-` | `/` | `*` | `min` | `max` | `if` },
                })
              }
              items={[
                { value: `+`, label: `+` },
                { value: `-`, label: `-` },
                { value: `/`, label: `/` },
                { value: `*`, label: `*` },
                { value: `min`, label: `min` },
                { value: `max`, label: `max` },
                { value: `if`, label: `if` },
                { value: `ifnot`, label: `ifnot` },
              ]}
            />
          </Grid>
        </Grid>
      )}

      <h3>{t(`Filter`)}</h3>
      <Grid container spacing={2}>
        <Grid size={{ xs: 4 }}>
          <Select
            label={t(`Aggregate method`)}
            value={datasetInstruction.filter?.aggregate}
            onChange={(aggregate) =>
              setDatasetInstruction({
                ...datasetInstruction,
                filter: {
                  ...datasetInstruction.filter,
                  aggregate: aggregate as DatasetInstructionAggregate,
                },
              })
            }
            items={[
              { value: ``, label: t(`None`) },
              { value: `sum`, label: t(`Sum`) },
              { value: `mean`, label: t(`Mean`) },
              { value: `min`, label: t(`Min`) },
              { value: `max`, label: t(`Max`) },
              { value: `latest`, label: t(`Latest`) },
            ]}
          />
        </Grid>
      </Grid>

      <br />

      <Inline spaceBetween>
        <Button primary onClick={onSubmit}>
          {t(`Add dataset`)}
        </Button>

        {new Set(['calc']).has(datasetInstruction.type) && (
          <Grid size={{ xs: 4 }}>
            <TextField
              variant="standard"
              error={!datasetInstruction.return_id}
              label={t(`Return ID`)}
              value={datasetInstruction.return_id}
              onChange={(return_id) => {
                // TextField onChange can return a number, so we ensure it's a string
                setDatasetInstruction({ ...datasetInstruction, return_id: `${return_id}` })
              }}
            />
          </Grid>
        )}
      </Inline>

      <br />
      <br />
    </div>
  )
}
